import { get } from "lodash";
import { isEmpty } from "radash";
import { toast } from "react-toastify";
import { removeAllChars } from "src/services/utils";
import _ from "lodash";
import { useTheme, useMediaQuery } from "@mui/material";

/**
 * Returns the full name and initials of a user.
 *
 * @param user - The user object containing firstName and lastName properties.
 * @returns An object with fullName and initials properties.
 */
export const getFullNameAndInitials = (entity: any) => {
  const firstName = get(entity, "firstName", "");
  const lastName = get(entity, "lastName", "");
  if (isEmpty(firstName) && isEmpty(lastName)) {
    return { fullName: "", initials: "" };
  }

  const firstInitial = firstName?.charAt(0);
  const lastInitial = lastName?.charAt(0);

  return {
    fullName: `${firstName} ${lastName}`.trim(),
    initials: `${firstInitial || ""}${lastInitial || ""}`.trim(),
  };
};

export const getName = (entity: any) => {
  return getFullNameAndInitials(entity).fullName;
};

/**
 * Gets the initials from a given name.
 * If the name contains multiple words, it returns the initials of the first two words.
 * If the name is a single word, it returns the first letter of that word.
 * If the name is not a string or is an empty string, it returns an empty string.
 *
 * @param {string} name - The full name from which to extract initials.
 * @returns {string} The initials of the name.
 */
export const getStringNameInitials = (name: string) => {
  if (!name || typeof name !== "string") return "";

  // Trim and split by whitespaces
  const [firstName, lastName] = name.trim().toUpperCase().split(/\s+/);

  // If there's only one word, return the first letter of that word
  if (firstName.length === 1) {
    return firstName[0] || "";
  }

  // Otherwise, return the first letter of the first two words
  const firstInitial = firstName[0] || "";
  const secondInitial = lastName[0] || "";

  return `${firstInitial}${secondInitial}`;
};

/**
 * Formats a phone number string to a more readable format.
 *
 * @param {string} phoneNumberString - The phone number string to format.
 * @returns {string} The formatted phone number string.
 */
export const formatPhoneNumber = (phoneNumberString?: string): string => {
  if (!phoneNumberString || phoneNumberString === "") return "No Phone Number";

  return phoneNumberString
    .replace(/\D/g, "")
    .replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3");
};

/**
 * Calculates the BMI of a user.
 *
 * @param height - The height of the user.
 * @param weight - The weight of the user.
 * @param unit - The unit of the height and weight.
 * @returns The BMI of the user.
 */
type CalculateBMIProps = {
  height: { feet?: string; inches?: string; cm?: string };
  weight: { pounds?: string; kg?: string };
  unit: "Imperial" | "Metric";
};

export const calculateBMI = ({ height, weight, unit, }: CalculateBMIProps) => {
  if (unit === "Imperial") {
    if (_.isNil(height.feet) || _.isNil(height.inches) || _.isNil(weight.pounds)) return 0;

    const heightInInches = parseInt(height.feet) * 12 + parseInt(height.inches);
    const weightInPounds = parseInt(weight.pounds);

    return (weightInPounds / heightInInches ** 2) * 703;
  } else if (unit === "Metric") {
    if (_.isNil(height.cm) || _.isNil(weight.kg)) return 0;

    const heightInCm = parseInt(height.cm);
    const weightInKg = parseInt(weight.kg);

    return weightInKg / (heightInCm / 100) ** 2;
  }

  return 0;
};

/**
 * Returns the record id from the provided object.
 *
 * @param record - The object containing the record id.
 * @returns {string} The record id if found, otherwise undefined.
 */
export const getRecordId = (record: any, fallback?: string) =>
  get(record, "_id", get(record, "id", fallback)) as string;

/**
 * Extracts and displays an error message using react-toastify.
 *
 * @param error - The error object to extract the message from.
 * @param message - The fallback message to display if the error object doesn't have a friendly message.
 */
export const extractError = (error: any, message?: string) => {
  if (error?.friendly) return toast.error(error.message);
  if (message) return toast.error(message);
};

/**
 * Removes null or empty string values from an object.
 * @param {Record<string, any>} obj - The object to be cleaned.
 * @returns {Record<string, any>} A new object with null or empty string values removed.
 */
export const cleanObj = (input: Record<string, any>) => {
  let result: Record<string, any> = {};

  // Iterate over the keys of the input object
  for (const key of Object.keys(input)) {
    const value = input[key];
    const isNullOrUndefined = value === null || value === undefined;
    const isEmptyString = typeof value === "string" && value.trim() === "";

    // Add key-value pair to result if value is not null, undefined, or empty string
    if (!isNullOrUndefined && !isEmptyString) {
      result[key] = value;
    }
  }

  return result;
};

/**
 * Logs info only if in a development environment
 * @param info
 */
export const devOnlyLog = (info: any) => {
  if (process.env.BUILD_ENV !== "prod") {
    // eslint-disable-next-line no-console
    console.log(info);
  }
};

/**
 * Converts a value or each value in an array to a number if possible.
 *
 * If the value is a string representing a valid number (including floating-point numbers),
 * it converts the string to a number. If the value cannot be converted to a number,
 * or if it's not a string, number, or array, the function returns the original value.
 *
 * @param {any} value - The value to be converted, can be a string, number, array, or any type.
 * @returns {number | any} - The converted number, array of numbers, or the original value if conversion is not possible.
 */
export const convertToNumber = (value: any): number | any => {
  // Handle array case: Convert each value in the array
  if (Array.isArray(value)) {
    return value.map(convertToNumber);
  }

  // Return the value directly if it's already a number or not a string
  if (typeof value === "number" || typeof value !== "string") {
    return value;
  }

  // Attempt to parse the string as a number
  const parsedValue = parseFloat(value);

  // Return the parsed number if valid, otherwise return the original value
  return Number.isNaN(parsedValue) ? value : parsedValue;
};

/**
 * Capitalizes the first letter of each word in the given string.
 *
 * @param {string} str - The input string to be transformed.
 * @returns {string} The transformed string with each word's first letter capitalized.
 */
export const capitalizeWords = (value: string) =>
  value.replace(/\b\w/g, (char) => char.toUpperCase());

export const handleDynamicFormSubmission = (formId: string) => {
  const form = document.getElementById(formId) as HTMLFormElement | null;
  if (form) {
    form.requestSubmit();
  } else {
    console.error(`Form with an id of${formId} not found`);
  }
};

export const getIsMobile = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  return isMobile;
};

/**
 * Scrolls to the first MUI form field with an error.
 *
 * This function finds the first element with the class "Mui-error" and scrolls it into view.
 * The scrolling is smooth and centers the element in the viewport.
 */
export const scrollToFirstErrorField = () => {
  setTimeout(() => {
    const firstErrorElement = document.querySelector(".Mui-error");
    if (firstErrorElement) {
      firstErrorElement.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, 100);
};
